import { yupResolver } from '@hookform/resolvers/yup';
import { InfoOutlined } from '@mui/icons-material';
import { CircularProgress, Grid } from '@mui/material';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { ICandidate, IJob, getJobByJobCode, inviteShareCandidateUsers } from 'wisestep-api';
import {
  ControlledTextArea,
  WsButton,
  WsColors,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';
import { array, object, string } from 'yup';

import {
  useDefaultSmtpEmail,
  useSurveyLogoUrl,
} from '../../../components/SurveyBuilder/SurveyCommonMethods';
import { success } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';

import ControlledShareNameAndEmailInput from './ControlledShareNameAndEmailInput';
import WidgetJobManagement from './WidgetJobManagement';
interface IShareCandidateOverlay {
  onClose: () => void;
  selectedJobData: IJob | null;
  candidate: ICandidate;
}

const ShareCandidateOverlay = ({ selectedJobData, onClose, candidate }: IShareCandidateOverlay) => {
  const [isLoading, setIsLoading] = useState(false);
  const fileUrlValue = useSurveyLogoUrl();
  const { defaultSmtpEmailValue, preferredProviderId } = useDefaultSmtpEmail();
  const setNotification = useNotificationStore((state) => state.setNotification);

  const methods = useForm({
    resolver: yupResolver(
      object().shape({
        contact: array()
          .of(
            object().shape({
              name: string().required('Please enter name'),
              email: string().required('Please enter email').email('Email is invalid'),
            })
          )
          .required('Please enter contact details properly'),
      })
    ),
  });

  const inviteUser = async (data) => {
    setIsLoading(true);
    let jobDataId = '';
    if (selectedJobData?.jobCode) {
      const res = await getJobByJobCode(selectedJobData?.jobCode);
      jobDataId = res.data.id;
    }
    if (data && selectedJobData?.jobCode) {
      await inviteShareCandidateUsers({
        to: data.candidates,
        subject: `Evaluation for ${selectedJobData?.title} || ${candidate?.name}`,
        body: data.description,
        interactionId: candidate?.candidateId as string,
        jobId: jobDataId,
        evalTemplateId: null,
        sharedCandidateName: candidate?.name,
        sharedJobTitle: selectedJobData?.title || '',
        recruiterEmail: defaultSmtpEmailValue ?? '',
        logoUrl: fileUrlValue ?? '',
        candidiateId: candidate?.candidateId ?? '',
        preferredProviderId: preferredProviderId,
      });
      setNotification(success('CV sent successfully.'));
    }
    onClose();
    setIsLoading(false);
  };

  const onSubmit = async (data) => {
    setIsLoading(true);
    const candidateInfo = {
      candidates: data.contact,
      description: data.description,
      status: 'view',
    };
    inviteUser(candidateInfo);
  };

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(onSubmit)}
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          overflow: 'hidden',
        }}
      >
        <Grid
          container
          p={1.5}
          sx={{
            border: `1px solid ${WsColors.TertiaryShades100}`,
            flexShrink: 0,
          }}
        >
          <Grid item xs={7} pl={3}>
            <WsTypography variant={WsTypoVariants.TITLE} color={WsTypoColors.PRIMARY} bold={true}>
              Share Candidate
            </WsTypography>
          </Grid>
          <Grid item xs={5} display="flex" justifyContent="flex-end">
            <WidgetJobManagement selectedJob={selectedJobData} ss={12} hideRemove={true} />
          </Grid>
        </Grid>
        <Grid
          item
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 4,
          }}
        >
          <Grid container>
            <Grid item xs={12} display="flex" alignItems="center" mb={2}>
              <InfoOutlined
                sx={{
                  fontSize: 24,
                  mr: 1,
                }}
              />
              <WsTypography display="inline" variant={WsTypoVariants.SUB_TITLE}>
                Share candidate CV.
              </WsTypography>
            </Grid>
            <Grid item xs={12}>
              <ControlledShareNameAndEmailInput name="contact" reset={() => {}} />
              <Grid container>
                <Grid item xs={12}>
                  <ControlledTextArea name="description" labelText="Description" minRows={5} />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid
          item
          sx={{
            background: WsColors.White,
            p: 1,
            display: 'flex',
            justifyContent: 'flex-end',
            borderTop: `1px solid ${WsColors.TertiaryShades100}`,
            flexShrink: 0,
          }}
        >
          <WsButton
            type="submit"
            disabled={isLoading}
            endIcon={isLoading ? <CircularProgress size={17} color="secondary" /> : <></>}
          >
            {isLoading ? 'Sending...' : 'Send'}
          </WsButton>
        </Grid>
      </form>
    </FormProvider>
  );
};

export default ShareCandidateOverlay;
